import React, { useEffect, useRef } from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledAboutSection = styled.section`
  max-width: 1200px;

  .inner {
    display: grid;
    grid-template-columns: 3fr 2fr;
    grid-gap: 50px;

    @media (max-width: 1080px) {
      display: block;
    }
  }
`;
const StyledText = styled.div`
  ul.highlights-list {
    display: block;
    padding: 0;
    margin: 20px 0 0 0;
    list-style: none;
    overflow: hidden;
  }

  ul.highlights-list li {
    position: relative;
    margin-bottom: 5px;
    padding-left: 22px;
    font-family: var(--font-mono);
    font-size: var(--fz-xs);
  }

  ul.highlights-list li:before {
    content: '▹';
    position: absolute;
    left: 0;
    top: 3px;
    color: var(--green);
    font-size: var(--fz-sm);
  }

  ul.highlights-list li .desc {
    font-family: var(--font-sans);
    font-size: var(--fz-sm);
    line-height: 1.35;
    margin-top: 3px;
    color: var(--slate);
  }
`;

const StyledPic = styled.div`
  position: relative;
  max-width: 300px;

  @media (max-width: 768px) {
    margin: 50px auto 0;
    width: 70%;
  }

  .wrapper {
    ${({ theme }) => theme.mixins.boxShadow};
    position: relative;
    width: 100%;
    display: block;
    border-radius: var(--border-radius);
    overflow: hidden;
    background: transparent;
    cursor: pointer;
  }

  .img {
    width: 100%;
    height: auto;
    display: block;
    border-radius: var(--border-radius);
    transition: opacity 0.35s ease;
  }

  /* default image visible */
  .default-photo {
    opacity: 1;
  }

  /* hover image stacked on top, hidden initially */
  .hover-photo {
    position: absolute;
    inset: 0;
    opacity: 0;
  }

  /* swap on hover */
  .wrapper:hover .default-photo {
    opacity: 0;
  }

  .wrapper:hover .hover-photo {
    opacity: 1;
  }
`;

const About = () => {
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, []);

  const highlights = [
    {
      title: 'CHIEF',
      link: 'https://www.nature.com/articles/s41586-024-07894-z',
      label: 'Wang&Zhao* et al., Nature 2024',
      desc: 'A pathology foundation model for cancer diagnosis and prognosis prediction',
    },
    {
      title: 'DAIMS',
      link: '',
      label: 'Cao&Ji et al.&Zhao^, Advanced Science 2026',
      desc:
        'A generalizable multimodal model for treatment-stratified risk and survival assessment in colorectal cancer under real-world constraints',
    },
    {
      title: 'PICTURE',
      link: 'https://www.nature.com/articles/s41467-025-64249-6',
      label: 'Zhao*&Lin et al., Nature Communications 2025',
      desc:
        'Uncertainty-aware ensemble of foundation models differentiates glioblastoma from its mimics',
    },

    {
      title: 'Epilepsy PA',
      link: 'https://www.nature.com/articles/s41467-025-64805-0',
      label: 'Fan&Zhao^ et al., Nature Communications 2025',
      desc:
        'Physical activity levels may impact on the risk of premature mortality in people with epilepsy',
    },
    {
      title: 'CHARM',
      link: 'https://www.sciencedirect.com/science/article/pii/S2666634023001897',
      label: 'Nasrallah&Zhao* et al., Med 2023',
      desc:
        'Machine learning for cryosection pathology predicts the 2021 WHO classification of glioma',
    },
    {
      title: 'PhaseFIT',
      link: 'https://www.nature.com/articles/s41377-023-01296-y',
      label: 'Zhao&Wang&Zhu et al., Light: Science&Applications 2023',
      desc: 'PhaseFIT: live-organoid phase-fluorescent image transformation via generative AI',
    },
  ];

  return (
    <StyledAboutSection id="about" ref={revealContainer}>
      <h2 className="numbered-heading">About Me</h2>

      <div className="inner">
        <StyledText>
          <div>
            <p>
              I am Junhan Zhao, Ph.D., an Assistant Professor in Pediatrics (Principal Investigator,
              Research-Track), Comprehnensive Cancer Center, Section of Biomedical Informatics at{' '}
              <a href="https://www.uchicago.edu/en">the University of Chicago</a>,{' '}
              <a href="https://commons.cri.uchicago.edu/">Data for the Common Good (D4CG)</a> ,
              where we are commited to building communities, platforms, and ecosystems that maximize
              the potential of data to drive discovery and improve human health. I am a visiting
              scholar scientist at{' '}
              <a href="https://dbmi.hms.harvard.edu/">
                Harvard Medical School - Department of Biomedical Informatics
              </a>
              , where I was trained in 2020 (PhD thesis), 2021-2025 (Postdoc). I am also a visiting
              researcher at{' '}
              <a href="https://www.massgeneral.org/surgery">
                Massachusetts General Hospital - Surgery
              </a>{' '}
              and{' '}
              <a href="https://spacemedresearch.mgh.harvard.edu/">
                Center for Space Medicine Research
              </a>{' '}
              for collaborative mechanism-related biomedical studies. I received{' '}
              <a href="https://global.uchicago.edu/pgfa/hong-kong/2026-27">
                2026 UChicago Provost’s Global Faculty Award
              </a>
              ,
              <a href="https://www.cancerresearchfdn.org/recipient/junhan-zhao-phd/?back=young-investigator-awards">
                Cancer Research Foundation Young Investigator Award 2026
              </a>
              ,{' '}
              <a href="https://www.nvidia.com/en-us/industries/higher-education-research/academic-grant-program/">
                Nvidia Academic Grant Award
              </a>
              , and{' '}
              <a href="https://www.anthropic.com/news/rare-disease-research-grants">
                Anthropic AI for Science
              </a>
              , and{' '}
              <a href="https://www.nsf.gov/funding/initiatives/i-corps/updates/nsf-announces-special-opportunity-i-corpstm-quantumx">
                NSF Quantum+X
              </a>
              .
            </p>

            <p>
              I earned my Ph.D. in computer graphics as a Bilsland Fellowship awardee and served as
              an undergraduate core course instructor at{' '}
              <a href="https://www.topuniversities.com/universities/purdue-university">
                {' '}
                Purdue University
              </a>
              . Prior to this, I proudly completed my engineering training and graduated with
              distinction from{' '}
              <a href="https://www.topuniversities.com/universities/shanghai-jiao-tong-university">
                Shanghai Jiao Tong University
              </a>{' '}
              and{' '}
              <a href="https://www.topuniversities.com/universities/cornell-university">
                {' '}
                Cornell University{' '}
              </a>
              . I also hold a M.Sc in Biostatistics from{' '}
              <a href="https://www.topuniversities.com/universities/harvard-university">
                {' '}
                Harvard University{' '}
              </a>
              .
            </p>
            <p></p>
            <p>
              <em>
                <u>I’m not a fan of sleep but I do enjoy a good whiskey</u>, so I channel that extra
                energy into bringing technology to real-world practice
              </em>
              . I co-founded <a href="https://alvushealth.com/"> Alvus Health</a> and advise{' '}
              <a href="https://encapsulate.bio/"> Encapsulate </a> supported by{' '}
              <a href="https://www.nasa.gov/international-space-station/space-station-research-and-technology/in-space-production-applications/">
                {' '}
                NASA InSPA
              </a>{' '}
              and serve on the Research Advisory Board of{' '}
              <a href="https://www.shrinerschildrens.org/en"> Shriners Children's</a>. I was
              involved in <a href="https://pison.com/"> Pison Technology </a> and{' '}
              <a href="https://www.simulicity.com/"> Simulicity</a> at their early stages. I briefly
              worked at <a href="https://www.bosch.us/our-company/innovation/"> Bosch Research</a>.
              I am a junior editor for{' '}
              <a href="https://spj.science.org/page/bmef/about"> BME Frontiers </a>,{' '}
              <a href="https://academic.oup.com/gigascience"> GigaScience </a> and{' '}
              <a href="https://accscience.com/journal/OR"> Organoid Research</a>. I serve as an
              academic editor for{' '}
              <a href="https://journals.plos.org/digitalhealth/static/editorial-board">
                {' '}
                PLOS Digital Health
              </a>{' '}
              and an associcate editor for{' '}
              <a href="https://loop.frontiersin.org/people/3009668/overview">
                {' '}
                Frontiers in Medicine
              </a>
              .
            </p>

            <p>
              I’ve inked over <a href="https://orcid.org/0000-0002-0316-8365"> 55 </a> peer-reviewed
              papers in AI and biomedicine. Selected highlights where I served as first (*) or
              corresponding (^) author:
            </p>
          </div>

          <ul className="highlights-list">
            {highlights.map((h, i) => (
              <li key={i}>
                <strong>{h.title}</strong> (
                <a href={h.link} target="_blank" rel="noopener noreferrer">
                  {h.label}
                </a>
                )<div className="desc">{h.desc}</div>
              </li>
            ))}
          </ul>
        </StyledText>

        <StyledPic>
          <div className="wrapper">
            {/* default: official headshot */}
            <StaticImage
              className="img default-photo"
              src="../../images/y.jpg"
              width={500}
              quality={95}
              formats={['AUTO', 'WEBP', 'AVIF']}
              alt="Official headshot"
            />

            {/* on hover: casual headshot */}
            <StaticImage
              className="img hover-photo"
              src="../../images/me_official.jpg"
              width={500}
              quality={95}
              formats={['AUTO', 'WEBP', 'AVIF']}
              alt="Casual headshot"
            />
          </div>
        </StyledPic>
      </div>
    </StyledAboutSection>
  );
};

export default About;
